import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { createStyles, Group, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconStarFilled } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import useProducts from 'hooks/product/useProducts';
import { CellProps } from 'react-table';
import { CypressProps, ProductRetrieve } from 'types';

import ModalRetailDelete from './ModalRetailDelete';

const useStyles = createStyles(theme => ({
  tableRow: {
    cursor: 'pointer',
  },
  tableRowSelected: {
    background: theme.fn.rgba(theme.colors.kiloBlue[2], 0.5),
    cursor: 'pointer',
  },
}));

const RetailTable = ({
  search,
  orderByFavorites,
  openDrawer,
  setRetailId,
  cartItems,
  setCartItems,
  productMutatePerm = true,
  invoiceCreatePerm = true,
  bgc = 'white',
}: {
  search: string;
  orderByFavorites: boolean;
  openDrawer: () => void;
  setRetailId: (retailId: string) => void;
  cartItems: ProductRetrieve[];
  setCartItems: React.Dispatch<React.SetStateAction<ProductRetrieve[]>>;
  productMutatePerm?: boolean;
  invoiceCreatePerm?: boolean;
  bgc?: 'white' | 'gray';
}): JSX.Element => {
  const { classes, theme } = useStyles();
  const { formatCurrency } = useLocalization();

  const [isDeleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [selected, setSelected] = useState<ProductRetrieve | undefined>();

  const [tableData, setTableData] = useState<ProductRetrieve[]>([]);
  const [ordering, setOrdering] = useState('name');
  const [ascending, setAscending] = useState(true);

  const favoriteOrdering = orderByFavorites ? '-is_favorite,' : '';

  const { data, isSuccess, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProducts({
      searchTerm: search,
      type: 'RETAIL',
      ordering: `${favoriteOrdering}${ascending ? '' : '-'}${ordering}`,
    });

  const cartRetailIds = useMemo(() => cartItems.map(({ productId }) => productId), [cartItems]);
  const checkIsRetailExistInCart = (productId: string): boolean => cartRetailIds.includes(productId);

  const handleDelete = (retail: ProductRetrieve): void => {
    setSelected(retail);
    openDeleteModal();
  };

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setAscending(!ascending);
      setOrdering(header);
    },
    [ascending],
  );

  const handleRowClick = (product: ProductRetrieve): void => {
    if (invoiceCreatePerm) {
      if (checkIsRetailExistInCart(product.productId)) {
        setCartItems(prev => prev.filter(({ productId: id }) => id !== product.productId));
      } else {
        setCartItems(prev => [...prev, { ...product }]);
      }
    }
  };

  const columns = [
    {
      Header: '',
      accessor: 'isFavorite',
      Cell: ({ value }: CellProps<ProductRetrieve>) =>
        value ? (
          <IconStarFilled style={{ color: theme.colors.yellow[5] }} size={theme.fontSizes.xl} />
        ) : (
          <></>
        ),
    },
    {
      Header: 'Item Name',
      accessor: 'name',
      Cell: ({ row }: CellProps<ProductRetrieve>) => (
        <Text weight='bold' truncate>
          {row.original.name}
        </Text>
      ),
      maxWidth: '200px',
    },
    {
      Header: 'Price',
      accessor: 'price',
      Cell: ({ value }: CellProps<ProductRetrieve>) => (
        <Text>{formatCurrency(value ? value / 100 : 0)} </Text>
      ),
    },
    {
      id: 'actions',
      Cell: ({ row }: CellProps<ProductRetrieve> & CypressProps) => {
        return (
          <Group position='right' px='xs'>
            <Menu
              position='bottom-end'
              items={[
                {
                  title: 'Edit',
                  onClick: () => {
                    setRetailId(row.original.productId);
                    openDrawer();
                  },
                },
                {
                  title: 'Delete',
                  color: 'Red',
                  onClick: () => handleDelete(row.original),
                },
              ]}
              disabled={!productMutatePerm}
              data-cy='itemRowButton'
            />
          </Group>
        );
      },
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [data, isLoading]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['name', 'productPrice.price']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
        handleRowClick={handleRowClick}
        rowClassName={(product: ProductRetrieve) =>
          checkIsRetailExistInCart(product.productId) ? classes.tableRowSelected : classes.tableRow
        }
        bgc={bgc}
      />

      {selected && (
        <ModalRetailDelete
          opened={isDeleteModalOpened}
          handleClose={closeDeleteModal}
          retail={selected as ProductRetrieve}
          handleClearCart={() => setCartItems([])}
        />
      )}
    </>
  );
};

export default RetailTable;
